import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/context";
import { pendingChangesService } from "../services/pendingChangesService";

/**
 * Hook que devuelve la cantidad de cambios pendientes (status='pending')
 * para mostrar como badge en el navbar.
 *
 * Solo hace fetch si el usuario es admin del área Bienes (mismo gate que
 * el endpoint de backend rechaza con 403 a otros usuarios).
 *
 * Hace polling ligero cada `intervalMs` (default 60s) y refetch al volver
 * el foco a la pestaña.
 *
 * @param {object} options
 * @param {number} [options.intervalMs=60000]
 * @param {boolean} [options.enabled]
 */
export function usePendingChangesCount({ intervalMs = 60_000, enabled } = {}) {
  const { token, user, isAdmin } = useAuth();
  const isBienesAdmin = !!user && isAdmin?.() && user?.areaId === 1;
  const shouldFetch = enabled !== undefined ? enabled : isBienesAdmin;

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!shouldFetch || !token) {
      setCount(0);
      return;
    }
    try {
      setLoading(true);
      // limit=1 sólo necesitamos pagination.total
      const res = await pendingChangesService.list(
        { status: "pending", page: 1, limit: 1 },
        token,
      );
      const total =
        res?.pagination?.total ??
        res?.total ??
        (Array.isArray(res?.data) ? res.data.length : 0);
      setCount(Number(total) || 0);
    } catch {
      // Silencioso: si falla (token vencido, 403, red), dejamos el count en 0.
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [shouldFetch, token]);

  useEffect(() => {
    if (!shouldFetch) {
      setCount(0);
      return undefined;
    }
    refetch();
    const id = setInterval(refetch, intervalMs);
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [shouldFetch, intervalMs, refetch]);

  return { count, loading, refetch, enabled: shouldFetch };
}

export default usePendingChangesCount;
